import { Auth, Amplify } from 'aws-amplify';
import React, { createContext, useContext, useEffect, useState } from 'react';
import { AwsConfigAuth } from '../commons/auth';

Amplify.configure({ Auth: AwsConfigAuth });
interface SignInOpts {
  redirect?: string;
}
interface User {
  username: string;
  challengeParam: {
    isTaiwan: string;
  };
}

interface UseAuth {
  isLoading: boolean;
  isAuthenticated: boolean;
  username: string;
  signInUser: { user: User; opts?: SignInOpts } | undefined;
  signUp: (username: string, password: string, emailAddress: string) => Promise<Result>;
  confirmSignUp: (mfaCode: string) => Promise<Result>;
  signIn: (
    username: string,
    password: string,
    lang: string,
    signInOpts?: SignInOpts,
    recipient?: string
  ) => Promise<Result>;
  signOut: () => void;
  confirmMfaCode: (userinfo: { user: User; opts?: SignInOpts }, code: string) => Promise<Result>;
  changePassword: (oldPassword: string, newPassword: string) => Promise<Result>;
  resetPassword: (loginId: string) => Promise<Result>;
  confirmResetPassword: (loginId: string, code: string, newPassword: string) => Promise<Result>;
}

interface Result {
  success: boolean;
  message: string;
}

const authContext = createContext({} as UseAuth);

export const ProvideAuth: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const auth = useProvideAuth();
  return <authContext.Provider value={auth}>{children}</authContext.Provider>;
};

export const useAuth = () => {
  return useContext(authContext);
};

const useProvideAuth = (): UseAuth => {
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [username, setUsername] = useState('');

  const [signInUser, setSignInUser] = useState<{
    user: User;
    opts?: SignInOpts;
  }>(); //サインインからMFA認証までのユーザ情報

  useEffect(() => {
    Auth.currentAuthenticatedUser()
      .then((result) => {
        setUsername(result.username);
        setIsAuthenticated(true);
        setIsLoading(false);
      })
      .catch(() => {
        setUsername('');
        setIsAuthenticated(false);
        setIsLoading(false);
      });
  }, []);

  const signUp = async (username: string, password: string, emailAddress: string) => {
    try {
      await Auth.signUp({
        username,
        password,
        attributes: {
          email: emailAddress,
        },
      });
      setUsername(username);
      return { success: true, message: '' };
    } catch (error) {
      if (error instanceof Error) {
        if (error.message === 'Network error') {
          return {
            success: false,
            message: 'NetworkError',
          };
        }
        const message = (error as { code?: string }).code || 'UnknownError';
        return {
          success: false,
          message: message,
        };
      }
      return {
        success: false,
        message: 'UnknownError',
      };
    }
  };

  const confirmSignUp = async (mfaCode: string) => {
    try {
      await Auth.confirmSignUp(username, mfaCode);
      // const result = await signIn(username, password);
      return { success: true, message: '' };
    } catch (error) {
      if (error instanceof Error) {
        if (error.message === 'Network error') {
          return {
            success: false,
            message: 'NetworkError',
          };
        }
        const message = (error as { code?: string }).code || 'UnknownError';
        return {
          success: false,
          message: message,
        };
      }
      return {
        success: false,
        message: 'UnknownError',
      };
    }
  };

  const signIn = async (
    username: string,
    password: string,
    lang: string,
    opts?: SignInOpts,
    recipient?: string
  ) => {
    try {
      const result = await Auth.signIn(username, password, {
        ...(recipient ? { recipient: '1' } : {}),
        domain: window.location.host,
        lang: lang,
      });
      setUsername(result.username);
      setSignInUser({ user: result, opts });
      console.log(result);
      return { success: true, message: '' };
    } catch (error) {
      if (error instanceof Error) {
        if (error.message === 'Network error') {
          return {
            success: false,
            message: 'NetworkError',
          };
        }
        if (error.message.includes('Socket timeout')) {
          return {
            success: false,
            message: 'TimeoutError',
          };
        }
        if (error.message.includes('Incorrect username or password.')) {
          return {
            success: false,
            message: 'NotAuthorizedException',
          };
        }
        if (error.message.includes('CreateAuthChallenge failed with error')) {
          const errorContent = error.message.split('CreateAuthChallenge failed with error ')[1];
          if (errorContent.includes('Invalid domain')) {
            return {
              success: false,
              message: 'InvalidDomain',
            };
          }
          if (errorContent.includes('failed to send verification code')) {
            return {
              success: false,
              message: 'FailedToSendVerificationCode',
            };
          }
          if (errorContent.includes('mail is empty')) {
            return {
              success: false,
              message: 'MailIsEmpty',
            };
          }
          if (errorContent.includes('Failed to get mfa info')) {
            return {
              success: false,
              message: 'FailedToGetMfaInfo',
            };
          }
        }
      }
      return {
        success: false,
        message: 'UnknownError',
      };
    }
  };

  const signOut = async () => {
    try {
      await Auth.signOut();
      setUsername('');
      setIsAuthenticated(false);
      return { success: true, message: '' };
    } catch (error) {
      if (error instanceof Error) {
        if (error.message === 'Network error') {
          return {
            success: false,
            message: 'NetworkError',
          };
        }
        const message = (error as { code?: string }).code || 'UnknownError';
        return {
          success: false,
          message: message,
        };
      }
      return {
        success: false,
        message: 'UnknownError',
      };
    }
  };

  const confirmMfaCode = async (userInfo: { user: User; opts?: SignInOpts }, code: string) => {
    try {
      const result = await Auth.sendCustomChallengeAnswer(userInfo.user, code);
      if (result.signInUserSession && result.signInUserSession.accessToken) {
        setIsAuthenticated(true);
        return { success: true, message: '' };
      } else {
        return { success: false, message: 'NotAuthorizedException' };
      }
    } catch (error) {
      if (error instanceof Error) {
        if (error.message === 'Network error') {
          return {
            success: false,
            message: 'NetworkError',
          };
        }
      }
      return {
        success: false,
        message: 'NotAuthorizedException',
      };
    }
  };

  const changePassword = async (oldPassword: string, password: string) => {
    try {
      const currentUser = await Auth.currentAuthenticatedUser();
      await Auth.changePassword(currentUser, oldPassword, password);
      return { success: true, message: '' };
    } catch (error) {
      if (error instanceof Error) {
        if (error.message === 'Network error') {
          return {
            success: false,
            message: 'NetworkError',
          };
        }
        const message = (error as { code?: string }).code || 'UnknownError';
        return {
          success: false,
          message: message,
        };
      }
      return {
        success: false,
        message: 'UnknownError',
      };
    }
  };

  const resetPassword = async (loginId: string) => {
    try {
      await Auth.forgotPassword(loginId);
      return { success: true, message: '' };
    } catch (error) {
      if (error instanceof Error) {
        if (error.message === 'Network error') {
          return {
            success: false,
            message: 'NetworkError',
          };
        }
      }
      return {
        success: false,
        message: 'UnexpectedError',
      };
    }
  };

  const confirmResetPassword = async (loginId: string, code: string, newPassword: string) => {
    try {
      await Auth.forgotPasswordSubmit(loginId, code, newPassword);
      return { success: true, message: '' };
    } catch (error) {
      if (error instanceof Error) {
        if (error.message === 'Network error') {
          return {
            success: false,
            message: 'NetworkError',
          };
        }
        const message = (error as { code?: string }).code || 'UnknownError';
        return {
          success: false,
          message: message,
        };
      }
      return {
        success: false,
        message: 'UnknownError',
      };
    }
  };

  return {
    isLoading,
    isAuthenticated,
    username,
    signInUser,
    signUp,
    confirmSignUp,
    signIn,
    signOut,
    confirmMfaCode,
    changePassword,
    resetPassword,
    confirmResetPassword,
  };
};
